import { describeAddFailure, describeDriverTemplate, traceCameraAdd } from '../../add-trace.js';
import { ensureNotNil } from '@studio/js-utils';
import type { Model } from '@studio/models/device';
import { isNotNil } from '@tryiris-ai/js-types';
import type { Template } from '../types.js';
import { mutex } from './constants.js';
import { registryTemplateCacheKey } from '../registry-config-cache.js';
import { fetchRegistrant } from '#~/api/device/registry/index.js';
import { depot } from '#~/depot/index.js';
import { pbToJsRegistryTemplate } from '#~/protobuf/index.js';

/**
 * Returns the registry template for the specified model.
 *
 * @param modelId - The ID of the model whose registry template is the be fetched.
 * @param token - Access token.
 * @returns The device's registry template.
 */
export const getTemplate = async (modelId: Model['id'], token: string): Promise<Template> => {
  const { produce, registry } = depot.getState();
  const cacheKey = registryTemplateCacheKey(modelId);

  // Have we already fetched the template for this model in this org scope?
  if (isNotNil(registry.templates[cacheKey])) {
    return ensureNotNil(registry.templates[cacheKey]);
  }

  // Fetch the template.
  return mutex.runExclusive(async () => {
    const scopedKey = registryTemplateCacheKey(modelId);
    const config = depot.getState().registry.templates[scopedKey];

    // Do we still need to run?
    if (isNotNil(config)) {
      return config;
    }

    // Fetch the template
    // Observe the actual fetch (including hook-prefetched templates) without logging API bodies.
    traceCameraAdd('fingerprint.registry-request', { modelId });
    let result: Template;
    try {
      result = pbToJsRegistryTemplate(await fetchRegistrant(modelId, token));
      traceCameraAdd('fingerprint.registry-resolved', {
        modelId,
        template: describeDriverTemplate(result)
      });
    } catch (error) {
      traceCameraAdd('fingerprint.registry-failed', {
        modelId,
        failure: describeAddFailure(error)
      });
      throw error;
    }

    produce((draft) => {
      draft.registry.templates[scopedKey] = result;
    });

    return result;
  });
};
